import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom'
import { AlertTriangle, Home, ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '../Button'

/**
 * RouteErrorBoundary component for React Router v6
 * Use this as the errorElement prop in route definitions
 *
 * Example:
 * {
 *   path: '/orders',
 *   element: <OrderList />,
 *   errorElement: <RouteErrorBoundary />
 * }
 */
const RouteErrorBoundary = () => {
  const error = useRouteError()
  const navigate = useNavigate()
  const { t } = useTranslation()

  let errorMessage: string
  let errorStatus: number | undefined
  let errorStatusText: string | undefined

  if (isRouteErrorResponse(error)) {
    errorMessage = error.data?.message || error.statusText
    errorStatus = error.status
    errorStatusText = error.statusText
  } else if (error instanceof Error) {
    errorMessage = error.message
  } else if (typeof error === 'string') {
    errorMessage = error
  } else {
    console.error(error)
    errorMessage = 'Unknown error'
  }

  const getErrorTitle = () => {
    if (errorStatus === 404) return t('errors.notFound', 'Page Not Found')
    if (errorStatus === 403) return t('errors.unauthorized', 'Access Denied')
    if (errorStatus === 500) return t('errors.generic', 'Server Error')
    return t('errors.generic', 'Oops! Something went wrong')
  }

  const getErrorDescription = () => {
    if (errorStatus === 404) {
      return "The page you're looking for doesn't exist or has been moved."
    }
    if (errorStatus === 403) {
      return "You don't have permission to access this page."
    }
    if (errorStatus === 500) {
      return 'Our server encountered an error. Please try again later.'
    }
    return "We're sorry, but something unexpected happened while loading this page."
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleGoHome = () => {
    navigate('/')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
          {/* Error Icon */}
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>

          {/* Error Status */}
          {errorStatus && (
            <div className="text-6xl font-bold text-primary-pink mb-2">{errorStatus}</div>
          )}

          {/* Error Title */}
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            {getErrorTitle()}
          </h1>

          {/* Error Description */}
          <p className="text-gray-600 dark:text-gray-400 mb-6">{getErrorDescription()}</p>

          {/* Error Details (only in development) */}
          {import.meta.env.DEV && (
            <details className="mb-6 text-left">
              <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400 hover:text-primary-pink mb-2">
                Technical Details
              </summary>
              <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 text-xs font-mono overflow-auto max-h-60">
                {errorStatus && (
                  <p className="mb-2">
                    <span className="font-semibold">Status:</span> {errorStatus} {errorStatusText}
                  </p>
                )}
                <p className="text-red-500 font-semibold mb-2">{errorMessage}</p>
                {error instanceof Error && error.stack && (
                  <pre className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {error.stack}
                  </pre>
                )}
              </div>
            </details>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={handleGoBack} variant="secondary" leftIcon={<ArrowLeft className="w-4 h-4" />}>
              {t('common.back', 'Go Back')}
            </Button>
            <Button onClick={handleGoHome} leftIcon={<Home className="w-4 h-4" />}>
              {t('nav.dashboard', 'Go Home')}
            </Button>
          </div>
        </div>

        {/* Support Information */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
          If this problem persists, please contact your consultant
        </p>
      </div>
    </div>
  )
}

export default RouteErrorBoundary
